import React from 'react';
import { Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails, useTheme, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SectionHeader from '../SectionHeader';

const FaqSection = () => {
  const theme = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const faqs = [
    {
      question: 'Как ДокМедЧат защищает конфиденциальность пациентов?',
      answer: 'ДокМедЧат использует сквозное шифрование для всех сообщений. Клиники могут разместить сервер внутри своей инфраструктуры для полного контроля над данными. Доступ к информации строго контролируется.',
    },
    {
      question: 'Могут ли родственники пациента связаться с врачом напрямую?',
      answer: 'Да, администратор клиники может подключить родственника к групповому чату. При этом личные контактные данные врачей не раскрываются, а родственники не могут напрямую звонить врачам.',
    },
    {
      question: 'Как врачи управляют временем при использовании ДокМедЧат?',
      answer: 'ДокМедЧат позволяет врачам отвечать на сообщения в удобное время, что снижает нагрузку по сравнению с личными встречами или звонками, экономя до 40% рабочего времени.',
    },
    {
      question: 'Что такое встроенный трекер задач?',
      answer: 'Трекер задач — функция, встроенная в профиль пациента. Врачи вносят рекомендации и назначения, а система автоматически отправляет уведомления о предстоящих процедурах.',
    },
    {
      question: 'Как организовано хранение данных?',
      answer: 'Клиника может установить сервер на своей территории для полного контроля над информацией или использовать облачное хранилище с высоким уровнем шифрования.',
    },
    {
      question: 'На каких устройствах доступен ДокМедЧат?',
      answer: 'Мобильные приложения для iOS (App Store) и Android (Google Play), а также веб-интерфейс для медицинского персонала.',
    },
    {
      question: 'Какие преимущества перед другими системами коммуникации?',
      answer: 'Полная защита личных данных врачей, конфиденциальность информации пациентов, доступность в России по разумной цене и специальная адаптация для российского рынка здравоохранения.',
    },
    {
      question: 'Как начать использовать ДокМедЧат в нашей клинике?',
      answer: 'Свяжитесь с нами через форму на сайте или по указанным контактам. Мы проведем презентацию, поможем с установкой и обучим персонал. Также предлагаем тестовый период.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <Box
      id="faq"
      sx={{
        py: 10,
        backgroundColor: theme.palette.background.default,
      }}
      ref={ref}
    >
      <Container maxWidth="lg">
        <SectionHeader
          title="Часто задаваемые вопросы"
          subtitle="Ответы на распространенные вопросы о ДокМедЧат и его функциях."
        />

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {faqs.map((faq, index) => (
            <motion.div key={index} variants={childVariants}>
              <Accordion
                sx={{
                  mb: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  '&:before': {
                    display: 'none',
                  },
                  borderRadius: '8px !important',
                  overflow: 'hidden',
                  '&.Mui-expanded': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                  sx={{
                    backgroundColor: 'rgba(46, 80, 119, 0.03)',
                    '&.Mui-expanded': {
                      backgroundColor: 'rgba(46, 80, 119, 0.05)',
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ py: 2 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </motion.div>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
              Остались вопросы?
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ fontWeight: 600 }}
            >
              Связаться с нами
            </Button>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default FaqSection; 